import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Brain, CalendarClock, CheckSquare, Clock3, FileStack, Search, Trophy, Users } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import AiDecisionNotice from "../components/AiDecisionNotice";
import {
  localizedCriteriaReason,
  localizedLanguageEducationDetail,
  scoringLabel,
  scoringLanguageFromText,
} from "../utils/localizedScoringLabels";

const BULK_SCAN_STATE_KEY = "recruitglance_bulk_scan_state";
const BULK_SCAN_STATE_TTL_MS = 30 * 60 * 1000;
const MAX_BULK_SCAN_CANDIDATES = 100;

export default function JobCandidateScan() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams();
  const [searchParams] = useSearchParams();
  const progressRef = useRef(null);
  const enrichmentDebugRunRef = useRef(0);
  const enrichmentPollIntervalRef = useRef(null);
  const activeEnrichmentKeyRef = useRef("");
  const initialJobId = routeJobId || searchParams.get("job_id") || "";
  const candidateIdsParam = searchParams.get("candidate_ids") || "";
  const sourceParam = searchParams.get("source") || "";
  const highlightedCandidateIds = useMemo(
    () =>
      candidateIdsParam
        .split(",")
        .map((id) => Number(id))
        .filter(Boolean),
    [candidateIdsParam]
  );
  const isNewlyImportedFlow = sourceParam === "newly_imported";

  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(initialJobId);
  const [job, setJob] = useState(null);
  const [jobScannedFilenames, setJobScannedFilenames] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [query, setQuery] = useState("");
  const [fetching, setFetching] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [scanStartedAt, setScanStartedAt] = useState(null);
  const [scanElapsedSeconds, setScanElapsedSeconds] = useState(0);
  const [shortlisted, setShortlisted] = useState([]);
  const [notes, setNotes] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedResultCards, setExpandedResultCards] = useState({});
  const [activeDecisionTabs, setActiveDecisionTabs] = useState({});
  const [analyticsData, setAnalyticsData] = useState({});
  const [loadingAnalytics, setLoadingAnalytics] = useState(null);
  const [loadingInterviewQuestions, setLoadingInterviewQuestions] = useState({});
  const [scansLeft, setScansLeft] = useState(null);
  const [enrichmentProgress, setEnrichmentProgress] = useState({
    active: false,
    enriched: 0,
    total: 0,
    message: "",
  });

  useLongActionNotice(scanning, "Bulk scan", 45000);
  useLongActionNotice(enrichmentProgress.active, "AI enrichment", 60000);

  useEffect(() => {
    if (!scanStartedAt || (!scanning && !enrichmentProgress.active)) {
      return undefined;
    }

    const updateElapsed = () => {
      setScanElapsedSeconds(Math.max(0, Math.floor((Date.now() - scanStartedAt) / 1000)));
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);

    return () => window.clearInterval(intervalId);
  }, [scanStartedAt, scanning, enrichmentProgress.active]);

  const publishBulkScanState = (state, options = {}) => {
    const { notify = true } = options;

    if (!state) {
      localStorage.removeItem(BULK_SCAN_STATE_KEY);
    } else {
      localStorage.setItem(BULK_SCAN_STATE_KEY, JSON.stringify(state));
    }

    if (notify) {
      window.dispatchEvent(new Event("recruitglance:bulk-scan-status"));
    }
  };

  const clearBulkScanState = () => publishBulkScanState(null);

  const formatElapsedTime = (seconds) => {
    const safeSeconds = Math.max(0, seconds || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const loadCandidates = async () => {
    const res = await apiFetch("/candidates/");
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.detail || "Failed to load candidates.");
    }

    setCandidates(data || []);
    return data || [];
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    try {
      const savedState = JSON.parse(localStorage.getItem(BULK_SCAN_STATE_KEY) || "null");
      if (!savedState?.started_at) return;

      const startedAt = new Date(savedState.started_at).getTime();
      const expired = Number.isNaN(startedAt) || Date.now() - startedAt > BULK_SCAN_STATE_TTL_MS;

      if (expired) {
        clearBulkScanState();
        return;
      }

      if (savedState.job_id) {
        setSelectedJobId(String(savedState.job_id));
      }

      setScanStartedAt(startedAt);
      setScanElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));

      if (Array.isArray(savedState.selected_ids)) {
        setSelectedIds(savedState.selected_ids);
      }

      if (savedState.results) {
        setResults(savedState.results);
        setSelectedResultIds(
          (savedState.results.ranked_candidates || [])
            .map((candidate) => candidate.scan_id)
            .filter(Boolean)
        );
        pollTopCandidateEnrichment(savedState.results, "initial_saved_state_restore");
        return;
      }

      if (savedState.status === "scanning") {
        setScanning(true);
        setEnrichmentProgress({
          active: false,
          enriched: 0,
          total: 0,
          message: "Bulk scan is still running. Waiting for ranking results.",
        });
        setTimeout(() => {
          progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 120);
      }
    } catch {
      clearBulkScanState();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadPageData = async () => {
      setFetching(true);

      try {
        const [jobsRes, , dashboardRes] = await Promise.all([
          apiFetch("/jobs"),
          loadCandidates(),
          apiFetch("/dashboard/my_scans/"),
        ]);

        const jobsData = await jobsRes.json();

        if (!jobsRes.ok) {
          toast.error(jobsData?.detail || "Failed to load jobs.");
          navigate("/jobs");
          return;
        }

        setJobs(Array.isArray(jobsData) ? jobsData : []);

        if (dashboardRes.ok) {
          const dashboardData = await dashboardRes.json();
          setScansLeft(dashboardData.scans_left);
        }

        if (!selectedJobId) {
          setJob(null);
          return;
        }

        const jobRes = await apiFetch(`/jobs/${selectedJobId}/details`);
        const jobData = await jobRes.json();

        if (!jobRes.ok) {
          toast.error(jobData?.detail || "Failed to load selected job.");
          setJob(null);
          return;
        }

        setJob(jobData.job);
        setJobScannedFilenames((jobData.candidates || []).map((candidate) => candidate.filename).filter(Boolean));
      } catch (err) {
        toast.error(err.message || "Failed to load scan workspace.");
      } finally {
        setFetching(false);
      }
    };

    loadPageData();
  }, [user, selectedJobId, navigate]);

  useEffect(() => {
    if (!highlightedCandidateIds.length || !candidates.length) return;

    const availableIds = new Set(candidates.map((candidate) => candidate.candidate_id));
    const readyIds = new Set(
      candidates
        .filter((candidate) => isCandidateReady(candidate))
        .map((candidate) => candidate.candidate_id)
    );
    const selectableIds = highlightedCandidateIds.filter((candidateId) =>
      availableIds.has(candidateId) && readyIds.has(candidateId)
    );

    if (!selectableIds.length) return;

    setSelectedIds((prev) => Array.from(new Set([...prev, ...selectableIds])));
  }, [candidates, highlightedCandidateIds]);

  useEffect(() => {
    if (!candidates.some((candidate) => !isCandidateReady(candidate))) return;

    const intervalId = window.setInterval(() => {
      loadCandidates().catch(() => {});
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return candidates;

    return candidates.filter((candidate) => {
      const text = [
        candidate.name,
        candidate.latest_filename,
        candidate.email,
        candidate.phone,
        candidate.latest_job_name,
        ...(candidate.skills || []),
        ...(candidate.jobs || []).map((item) => item.job_name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(normalizedQuery);
    });
  }, [candidates, query]);

  const getJobApplicantCandidateId = (candidate) => {
    const jobEntry = (candidate.jobs || []).find((item) => String(item.job_id) === String(selectedJobId));
    return jobEntry ? candidate.candidate_id : null;
  };

  const jobApplicantCandidates = useMemo(
    () => filteredCandidates.filter((candidate) => getJobApplicantCandidateId(candidate)),
    [filteredCandidates, selectedJobId]
  );

  const generalApplicantCandidates = useMemo(
    () => filteredCandidates.filter((candidate) => (
      candidate.is_general_applicant && candidate.candidate_id && !getJobApplicantCandidateId(candidate)
    )),
    [filteredCandidates, selectedJobId]
  );

  const newlyImportedCandidates = useMemo(() => {
    if (!highlightedCandidateIds.length) return [];

    const highlightedSet = new Set(highlightedCandidateIds);
    return candidates.filter((candidate) => highlightedSet.has(candidate.candidate_id));
  }, [candidates, highlightedCandidateIds]);

  const isHighlightedCandidate = (candidate) =>
    highlightedCandidateIds.includes(candidate.candidate_id);

  const getEnrichmentStatus = (candidate) => candidate.enrichment_status || "ready";
  const isCandidateReady = (candidate) =>
    candidate.is_ready_for_scan !== false && getEnrichmentStatus(candidate) === "ready";

  const pendingJobApplicantCandidates = useMemo(() => {
    const scannedNames = new Set(jobScannedFilenames);

    return jobApplicantCandidates.filter((candidate) => {
      const filename = candidate.latest_filename || candidate.filename;
      return filename && !scannedNames.has(filename) && isCandidateReady(candidate);
    });
  }, [jobApplicantCandidates, jobScannedFilenames]);
  const bulkScanLimitExceeded = selectedIds.length > MAX_BULK_SCAN_CANDIDATES;

  const toggleCandidate = (scanId) => {
    const candidate = candidates.find((item) => item.candidate_id === scanId);
    if (candidate && !isCandidateReady(candidate)) {
      toast.error("This candidate is still being prepared for AI matching.");
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(scanId)
        ? prev.filter((id) => id !== scanId)
        : [...prev, scanId]
    );
  };

  const toggleVisibleCandidates = () => {
    const visibleIds = [
      ...jobApplicantCandidates.map((candidate) => getJobApplicantCandidateId(candidate)),
      ...generalApplicantCandidates.map((candidate) => candidate.candidate_id),
    ]
      .filter(Boolean);
    const readyIds = visibleIds.filter((id) => {
      const candidate = candidates.find((item) => item.candidate_id === id);
      return candidate && isCandidateReady(candidate);
    });

    const allVisibleSelected = readyIds.every((id) => selectedIds.includes(id));

    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !readyIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...readyIds])));
  };

  const selectPendingJobCandidates = () => {
    const pendingIds = pendingJobApplicantCandidates
      .map((candidate) => candidate.candidate_id)
      .filter(Boolean)
      .slice(0, MAX_BULK_SCAN_CANDIDATES);

    if (!pendingIds.length) {
      toast("No pending job applicants are ready to scan.");
      return;
    }

    setSelectedIds(pendingIds);
    toast.success(`${pendingIds.length} pending applicant${pendingIds.length === 1 ? "" : "s"} selected.`);
  };

  const updateCandidateEnrichment = (status) => {
    if (!status?.scan_id) return;

    setAnalyticsData((prev) => ({
      ...prev,
      [status.scan_id]: status,
    }));

    setResults((prev) => {
      if (!prev) return prev;

      const updateList = (list = []) =>
        list.map((candidate) => {
          if (candidate.scan_id !== status.scan_id) return candidate;

          const updatedCandidate = {
            ...candidate,
            ai_summary: status.ai_summary,
            llm_strengths: status.llm_strengths || [],
            llm_gaps: status.llm_gaps || [],
            interview_questions: status.interview_questions || [],
            skill_evidence: status.skill_evidence || [],
            language_match: status.language_match || [],
            top_qualification: status.top_qualification || candidate.top_qualification,
            scoring_breakdown: status.scoring_breakdown || candidate.scoring_breakdown,
          };

          return {
            ...updatedCandidate,
            candidate_summary: {
              ...(candidate.candidate_summary || {}),
              ai_summary: status.ai_summary,
              llm_strengths: status.llm_strengths || [],
              llm_gaps: status.llm_gaps || [],
              interview_questions: status.interview_questions || [],
              skill_evidence: status.skill_evidence || [],
              language_match: status.language_match || [],
              top_qualification: status.top_qualification || candidate.candidate_summary?.top_qualification,
              scoring_breakdown: status.scoring_breakdown || candidate.candidate_summary?.scoring_breakdown,
            },
          };
        });

      return {
        ...prev,
        top_candidates: updateList(prev.top_candidates),
        remaining_candidates: updateList(prev.remaining_candidates),
        ranked_candidates: updateList(prev.ranked_candidates),
      };
    });
  };

  const pollTopCandidateEnrichment = (scanResults, trigger = "unknown") => {
    const topScanIds = (scanResults?.top_candidates || [])
      .map((candidate) => candidate.scan_id)
      .filter(Boolean);
    const enrichmentKey = topScanIds.join(",");
    const runId = enrichmentDebugRunRef.current + 1;
    enrichmentDebugRunRef.current = runId;

    console.log("[RecruitGlance enrichment poll] start requested", {
      runId,
      trigger,
      topScanIds,
      topScanCount: topScanIds.length,
      jobId: selectedJobId || scanResults?.job?.id,
      selectedIdsCount: selectedIds.length,
      hasResults: Boolean(results),
      timestamp: new Date().toISOString(),
    });

    if (!topScanIds.length) {
      console.log("[RecruitGlance enrichment poll] stop before interval: no top scan ids", {
        runId,
        trigger,
      });
      clearBulkScanState();
      return;
    }

    if (activeEnrichmentKeyRef.current === enrichmentKey && enrichmentPollIntervalRef.current) {
      console.log("[RecruitGlance enrichment poll] ignored duplicate start", {
        runId,
        trigger,
        enrichmentKey,
        activeEnrichmentKey: activeEnrichmentKeyRef.current,
        activeIntervalId: enrichmentPollIntervalRef.current,
      });
      return;
    }

    if (enrichmentPollIntervalRef.current) {
      console.log("[RecruitGlance enrichment poll] clearing previous interval before new start", {
        runId,
        trigger,
        previousIntervalId: enrichmentPollIntervalRef.current,
        previousKey: activeEnrichmentKeyRef.current,
        newKey: enrichmentKey,
      });
      clearInterval(enrichmentPollIntervalRef.current);
      enrichmentPollIntervalRef.current = null;
      activeEnrichmentKeyRef.current = "";
    }

    activeEnrichmentKeyRef.current = enrichmentKey;

    setEnrichmentProgress({
      active: true,
      enriched: 0,
      total: topScanIds.length,
      message: "Fast ranking is ready. AI enrichment is running for the top candidates.",
    });
    publishBulkScanState({
      status: "enriching",
      job_id: selectedJobId || scanResults?.job?.id,
      selected_ids: selectedIds,
      started_at: scanStartedAt ? new Date(scanStartedAt).toISOString() : new Date().toISOString(),
      top_scan_ids: topScanIds,
      results: scanResults,
    }, { notify: false });

    let attempts = 0;
    const maxAttempts = 72;

    const intervalId = setInterval(async () => {
      attempts += 1;
      console.log("[RecruitGlance enrichment poll] tick", {
        runId,
        trigger,
        attempts,
        intervalId,
        topScanIds,
        timestamp: new Date().toISOString(),
      });

      try {
        const res = await apiFetch(`/scans/enrichment-status?ids=${encodeURIComponent(topScanIds.join(","))}`);
        console.log("[RecruitGlance enrichment poll] batch response", {
          runId,
          trigger,
          attempts,
          ok: res.ok,
          status: res.status,
          scanCount: topScanIds.length,
        });

        const data = res.ok ? await res.json() : { statuses: [] };
        const statuses = Array.isArray(data.statuses) ? data.statuses : [];

        const enrichedStatuses = statuses.filter((status) => status?.llm_enriched);
        enrichedStatuses.forEach(updateCandidateEnrichment);

        const complete = enrichedStatuses.length >= topScanIds.length;
        setEnrichmentProgress({
          active: !complete && attempts < maxAttempts,
          enriched: enrichedStatuses.length,
          total: topScanIds.length,
          message: complete
            ? "AI enrichment completed for the top candidates."
            : "Fast ranking is ready. AI enrichment is still running in the background.",
        });

        if (complete || attempts >= maxAttempts) {
          console.log("[RecruitGlance enrichment poll] stop", {
            runId,
            trigger,
            reason: complete ? "complete" : "max_attempts",
            attempts,
            enriched: enrichedStatuses.length,
            total: topScanIds.length,
            intervalId,
          });
          clearInterval(intervalId);
          if (enrichmentPollIntervalRef.current === intervalId) {
            enrichmentPollIntervalRef.current = null;
            activeEnrichmentKeyRef.current = "";
          }
          if (complete) {
            clearBulkScanState();
          }
        }
      } catch (err) {
        console.log("[RecruitGlance enrichment poll] error", {
          runId,
          trigger,
          attempts,
          message: err?.message || String(err),
          intervalId,
        });
        if (attempts >= maxAttempts) {
          console.log("[RecruitGlance enrichment poll] stop", {
            runId,
            trigger,
            reason: "error_max_attempts",
            attempts,
            intervalId,
          });
          clearInterval(intervalId);
          if (enrichmentPollIntervalRef.current === intervalId) {
            enrichmentPollIntervalRef.current = null;
            activeEnrichmentKeyRef.current = "";
          }
          clearBulkScanState();
          setEnrichmentProgress((prev) => ({
            ...prev,
            active: false,
            message: "Fast ranking is ready. AI enrichment is still processing in the background.",
          }));
        }
      }
    }, 5000);

    enrichmentPollIntervalRef.current = intervalId;
  };

  useEffect(() => {
    return () => {
      if (enrichmentPollIntervalRef.current) {
        clearInterval(enrichmentPollIntervalRef.current);
        enrichmentPollIntervalRef.current = null;
        activeEnrichmentKeyRef.current = "";
      }
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const syncRunningBulkScan = () => {
      try {
        const savedState = JSON.parse(localStorage.getItem(BULK_SCAN_STATE_KEY) || "null");
        if (!savedState?.started_at) return;

        const startedAt = new Date(savedState.started_at).getTime();
        const expired = Number.isNaN(startedAt) || Date.now() - startedAt > BULK_SCAN_STATE_TTL_MS;

        if (expired) {
          clearBulkScanState();
          setScanning(false);
          return;
        }

        if (savedState.status === "scanning" && !results) {
          setScanning(true);
          return;
        }

        if (savedState.results && !results) {
          setScanning(false);
          setResults(savedState.results);
          setSelectedResultIds(
            (savedState.results.ranked_candidates || [])
              .map((candidate) => candidate.scan_id)
              .filter(Boolean)
          );
          pollTopCandidateEnrichment(savedState.results, "sync_running_bulk_scan_saved_results");
        }
      } catch {
        clearBulkScanState();
      }
    };

    window.addEventListener("recruitglance:bulk-scan-status", syncRunningBulkScan);
    window.addEventListener("storage", syncRunningBulkScan);

    return () => {
      window.removeEventListener("recruitglance:bulk-scan-status", syncRunningBulkScan);
      window.removeEventListener("storage", syncRunningBulkScan);
    };
  }, [user, results]);

  const handleScanSelected = async () => {
    if (!selectedJobId) {
      toast.error("Select a job before scanning candidates.");
      return;
    }

    if (!selectedIds.length) {
      toast.error("Select at least one candidate.");
      return;
    }

    if (bulkScanLimitExceeded) {
      toast.error(`You can scan up to ${MAX_BULK_SCAN_CANDIDATES} candidates at once. Please split larger scans into smaller batches.`);
      return;
    }

    if (job?.is_archived) {
      toast.error("Restore this job before scanning candidates.");
      return;
    }

    const selectedCandidates = candidates.filter((candidate) => selectedIds.includes(candidate.candidate_id));
    const notReadyCount = selectedCandidates.filter((candidate) => !isCandidateReady(candidate)).length;

    if (notReadyCount > 0) {
      toast.error(`${notReadyCount} selected candidate(s) are still being prepared. Please wait until enrichment is complete.`);
      return;
    }

    if (scansLeft !== null && scansLeft !== undefined && scansLeft <= 0) {
      toast.error("Your scan quota is finished for this billing cycle.");
      return;
    }

    const scannedNames = new Set(jobScannedFilenames);
    const alreadyScannedCount = selectedCandidates.filter((candidate) => {
      const filename = candidate.latest_filename || candidate.filename;
      return filename && scannedNames.has(filename);
    }).length;
    const newScanCount = Math.max(selectedIds.length - alreadyScannedCount, 0);

    if (scansLeft !== null && scansLeft !== undefined && newScanCount > scansLeft) {
      toast.error(`Not enough scans left. This action needs ${newScanCount} new scan(s), but you have ${scansLeft}.`);
      return;
    }

    const startedAt = Date.now();
    const startedAtIso = new Date(startedAt).toISOString();

    setScanning(true);
    setScanStartedAt(startedAt);
    setScanElapsedSeconds(0);
    setResults(null);
    publishBulkScanState({
      status: "scanning",
      job_id: selectedJobId,
      selected_ids: selectedIds,
      started_at: startedAtIso,
    });
    setTimeout(() => {
      progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    setEnrichmentProgress({
      active: false,
      enriched: 0,
      total: 0,
      message: "",
    });

    let scanCompleted = false;

    try {
      const res = await apiFetch(`/jobs/${selectedJobId}/scan-library`, {
        method: "POST",
        body: JSON.stringify({ candidate_ids: selectedIds }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Bulk scan", data?.detail));
        return;
      }

      setResults(data);
      scanCompleted = true;
      publishBulkScanState({
        status: "enriching",
        job_id: selectedJobId,
        selected_ids: selectedIds,
        started_at: startedAtIso,
        results: data,
      });
      setTimeout(() => {
        progressRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      pollTopCandidateEnrichment(data, "scan_success");
      const ranked = data.ranked_candidates || [];
      const scannedFilenames = selectedCandidates
        .map((candidate) => candidate.latest_filename || candidate.filename)
        .filter(Boolean);

      if (scannedFilenames.length) {
        setJobScannedFilenames((prev) =>
          Array.from(new Set([...prev, ...scannedFilenames]))
        );
      }

      await loadCandidates();
      setSelectedResultIds(ranked.map((candidate) => candidate.scan_id));
      setShortlisted(ranked.filter((candidate) => candidate.is_shortlisted));
      setNotes(
        ranked.reduce((acc, candidate) => {
          const recruiterNote = candidate.recruiter_note === "Scanned from candidate library"
            ? ""
            : candidate.recruiter_note;

          if (recruiterNote) acc[candidate.scan_id] = recruiterNote;
          return acc;
        }, {})
      );
      toast.success(`Scanned ${data.total || 0} candidates for this job.`);
    } catch (err) {
      toast.error(actionFailureMessage("Bulk scan", err.message));
    } finally {
      setScanning(false);
      if (!scanCompleted) {
        clearBulkScanState();
      }
    }
  };

  const scoreClass = (score) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-700";
    return "border-rose-200 bg-rose-50 text-rose-700";
  };

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const scoreBarClass = (score) => {
    if (score >= 75) return "bg-emerald-500";
    if (score >= 55) return "bg-amber-500";
    return "bg-rose-500";
  };

  const decisionLabel = (score) => {
    if (score >= 75) return "Strong Match";
    if (score >= 55) return "Good Match";
    return "Needs Review";
  };

  const decisionBadgeClass = (score) => {
    if (score >= 75) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (score >= 55) return "border-amber-200 bg-amber-50 text-amber-800";
    return "border-amber-300 bg-amber-100 text-amber-900 shadow-sm";
  };

  const topCandidates = results?.top_candidates || [];
  const remainingCandidates = results?.remaining_candidates || [];
  const rankedCandidates = results?.ranked_candidates || [];
  const scoringLanguage = scoringLanguageFromText(job?.description || results?.job?.description || "");
  const duplicateCandidates = rankedCandidates.filter((candidate) => candidate.is_duplicate);
  const quotaExceeded = scansLeft !== null && scansLeft !== undefined && scansLeft <= 0;
  const isSelectedJobArchived = Boolean(job?.is_archived);

  const getMatchingSkills = (candidate) =>
    candidate.matching_skills || candidate.candidate_summary?.key_strengths || [];

  const getMissingSkills = (candidate) =>
    candidate.missing_skills || candidate.candidate_summary?.missing_areas || [];

  const getSignalBreakdown = (candidate) =>
    candidate.scoring_breakdown ||
    candidate.candidate_summary?.scoring_breakdown ||
    {};

  const getDetectedSignals = (candidate) => {
    const signals = new Set(getMatchingSkills(candidate));
    const breakdown = getSignalBreakdown(candidate);

    [...(breakdown.must_have || []), ...(breakdown.preferred || [])].forEach((item) => {
      const score = Number(item?.score || 0);
      if (item?.skill && score > 0) {
        signals.add(`${item.skill} (${Math.round(score)}%)`);
      }
    });

    return Array.from(signals);
  };

  const getUnconfirmedSignals = (candidate) => {
    const signals = new Set(getMissingSkills(candidate));
    const breakdown = getSignalBreakdown(candidate);

    (breakdown.must_have || []).forEach((item) => {
      if (item?.skill && !item.passed) {
        signals.add(item.skill);
      }
    });

    (breakdown.preferred || []).forEach((item) => {
      if (item?.skill && !item.found) {
        signals.add(item.skill);
      }
    });

    return Array.from(signals);
  };

  const getApplicantTitles = (candidate) =>
    candidate.job_titles || candidate.candidate_summary?.job_titles || [];

  const getTopQualification = (candidate) =>
    candidate.top_qualification || candidate.candidate_summary?.top_qualification || "";

  const firstNonEmptyArray = (...values) =>
    values.find((value) => Array.isArray(value) && value.length > 0) || [];

  const getCandidateAiSummary = (candidate) =>
    analyticsData[candidate.scan_id]?.ai_summary ||
    candidate.ai_summary ||
    candidate.candidate_summary?.ai_summary ||
    candidate.candidate_summary?.reason ||
    "";

  const getAiStrengths = (candidate) =>
    firstNonEmptyArray(
      analyticsData[candidate.scan_id]?.strengths,
      analyticsData[candidate.scan_id]?.llm_strengths,
      candidate.llm_strengths,
      candidate.candidate_summary?.llm_strengths,
      candidate.candidate_summary?.strengths
    );

  const getAiGaps = (candidate) =>
    firstNonEmptyArray(
      analyticsData[candidate.scan_id]?.gaps,
      analyticsData[candidate.scan_id]?.llm_gaps,
      candidate.llm_gaps,
      candidate.candidate_summary?.llm_gaps,
      candidate.candidate_summary?.gaps
    );

  const getInterviewQuestions = (candidate) => {
    const questions =
      analyticsData[candidate.scan_id]?.interview_questions ||
      candidate.interview_questions ||
      candidate.candidate_summary?.interview_questions ||
      [];

    return Array.isArray(questions) ? questions : [];
  };

  const getSkillEvidence = (candidate) => {
    const evidence =
      analyticsData[candidate.scan_id]?.skill_evidence ||
      candidate.skill_evidence ||
      candidate.candidate_summary?.skill_evidence ||
      [];

    return Array.isArray(evidence) ? evidence.filter((item) => !isLanguageEvidenceItem(item)) : [];
  };

  const isLanguageEvidenceItem = (item) => {
    const terms = [
      "german",
      "deutsch",
      "english",
      "englisch",
      "spanish",
      "french",
      "italian",
      "tamil",
      "c1",
      "b2",
      "b1",
      "a2",
      "a1",
      "native speaker",
      "muttersprache",
    ];
    const text = [
      item?.skill,
      item?.language,
      item?.required_level,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return terms.some((term) => text.includes(term));
  };

  const isValidLanguageMatchItem = (item) => {
    if (!item || typeof item !== "object") return false;
    return isLanguageEvidenceItem(item);
  };

  const getLanguageMatch = (candidate) => {
    const explicit =
      analyticsData[candidate.scan_id]?.language_match ||
      candidate.language_match ||
      candidate.candidate_summary?.language_match ||
      [];

    const legacyLanguageRows =
      analyticsData[candidate.scan_id]?.skill_evidence ||
      candidate.skill_evidence ||
      candidate.candidate_summary?.skill_evidence ||
      [];

    const languageRows = Array.isArray(explicit) ? explicit.filter(isValidLanguageMatchItem) : [];
    const migratedRows = Array.isArray(legacyLanguageRows)
      ? legacyLanguageRows.filter(isLanguageEvidenceItem).map((item) => ({
          language: item.language || item.skill || "Language",
          required_level: item.required_level || "Not specified",
          fit_score: item.fit_score ?? 0,
          confidence: item.confidence || 0,
          status: item.status || "language signal",
          evidence: item.evidence || "",
        }))
      : [];

    return [...languageRows, ...migratedRows];
  };

  const getScoringBreakdown = (candidate) =>
    candidate.scoring_breakdown ||
    candidate.candidate_summary?.scoring_breakdown ||
    null;

  const getLanguageFitScore = (item) =>
    Math.max(0, Math.min(100, Number(item?.fit_score ?? 0) || 0));

  const getWeightedScoreBreakdown = (candidate) => {
    const breakdown = getScoringBreakdown(candidate);
    const languageRows = getLanguageMatch(candidate);
    const qualification = getTopQualification(candidate);
    const finalScore = Math.round(candidate.match_score || 0);

    const languageFitPercent = languageRows.length
      ? languageRows.reduce((sum, item) => sum + getLanguageFitScore(item), 0) / languageRows.length
      : null;
    const educationPercent = qualification ? 60 : 0;
    const educationLanguagePercent = Math.max(languageFitPercent ?? 0, educationPercent);
    const languageEducationDetail = localizedLanguageEducationDetail(qualification, languageRows, scoringLanguage);

    const toPoints = (value, max) => Math.round((Math.max(0, Math.min(100, Number(value) || 0)) / 100) * max);
    const languagePoints = toPoints(educationLanguagePercent, 10);
    const experiencePercent = breakdown?.experience_score ?? 0;
    const hasMustCriteria = breakdown?.must_score !== null && breakdown?.must_score !== undefined;
    const hasPreferredCriteria = breakdown?.preferred_score !== null && breakdown?.preferred_score !== undefined;

    if (!hasMustCriteria && !hasPreferredCriteria) {
      const semanticPercent = breakdown?.semantic_score ?? breakdown?.base_score ?? finalScore;
      const semanticPoints = Math.min(toPoints(semanticPercent, 30), finalScore);
      const supportPoints = Math.min(languagePoints, Math.max(0, finalScore - semanticPoints));
      const experiencePoints = Math.min(toPoints(experiencePercent, 25), Math.max(0, finalScore - semanticPoints - supportPoints));
      const skillPoints = Math.max(0, finalScore - semanticPoints - experiencePoints - supportPoints);

      return [
        {
          label: scoringLabel("Semantic relevance", scoringLanguage),
          points: semanticPoints,
          max: 30,
          available: true,
          detail: "How closely the CV text matches the job description overall, even when wording differs.",
        },
        {
          label: scoringLabel("Skill coverage", scoringLanguage),
          points: skillPoints,
          max: 35,
          available: true,
          detail: "How much of the detected job skill requirement is supported by skills found in the CV.",
        },
        {
          label: scoringLabel("Experience evidence", scoringLanguage),
          points: experiencePoints,
          max: 25,
          available: experiencePercent > 0,
          detail: "Whether required skills are proven inside work experience or project context, not only listed as keywords.",
        },
        {
          label: scoringLabel("Language / education", scoringLanguage),
          points: supportPoints,
          max: 10,
          available: supportPoints > 0,
          detail: languageEducationDetail,
        },
      ];
    }

    const rows = [
      {
        label: scoringLabel("Semantic relevance", scoringLanguage),
        points: toPoints(breakdown?.semantic_score ?? breakdown?.base_score ?? candidate.match_score ?? 0, 25),
        max: 25,
        available: true,
        detail: "How closely the CV text matches the job description before must-have and preferred criteria are applied.",
      },
      {
        label: scoringLabel("Must-have skills", scoringLanguage),
        points: toPoints(breakdown?.must_score ?? 0, 35),
        max: 35,
        available: breakdown?.must_score !== null && breakdown?.must_score !== undefined,
        detail: "Recruiter-selected critical skills. Weak evidence here can cap the final score.",
      },
      {
        label: scoringLabel("Preferred skills", scoringLanguage),
        points: toPoints(breakdown?.preferred_score ?? 0, 15),
        max: 15,
        available: breakdown?.preferred_score !== null && breakdown?.preferred_score !== undefined,
        detail: "Bonus skills that improve confidence, but should not reject a candidate by themselves.",
      },
      {
        label: scoringLabel("Experience evidence", scoringLanguage),
        points: toPoints(experiencePercent, 20),
        max: 20,
        available: experiencePercent > 0,
        detail: "Whether required skills are proven inside work experience or project context, not only listed as keywords.",
      },
      {
        label: scoringLabel("Language / education", scoringLanguage),
        points: toPoints(educationLanguagePercent, 5),
        max: 5,
        available: languageFitPercent !== null || Boolean(qualification),
        detail: languageEducationDetail,
      },
    ];

    return rows;
  };

  const renderScoreBreakdown = (candidate, score, compact = false) => {
    const rows = getWeightedScoreBreakdown(candidate);
    const breakdown = getScoringBreakdown(candidate);
    const needsHumanReview = Boolean(breakdown?.needs_human_review);

    return (
      <div className={`rounded-lg border border-emerald-200 bg-emerald-50 ${compact ? "p-3" : "p-4"}`}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-emerald-950">{scoringLabel("Score Breakdown", scoringLanguage)}</h4>
            <p className="mt-1 text-xs leading-5 text-emerald-700">
              {scoringLabel("Score rows show independent evidence signals. The final score is calculated separately from the full scoring model.", scoringLanguage)}
            </p>
          </div>
          <span className="w-fit rounded-full bg-white px-3 py-1 text-sm font-bold text-emerald-900">
            {scoringLabel("Final", scoringLanguage)} {score}%
          </span>
        </div>

        <div className="mt-3 rounded-md bg-white/85 p-3 font-mono text-sm text-slate-800">
          <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-slate-500">
            {scoringLabel("Breakdown", scoringLanguage)}
          </p>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.label} className="grid grid-cols-[1fr_auto] items-center gap-4">
                <span className="truncate font-sans font-medium text-slate-700">{row.label}</span>
                <span className={`font-bold tabular-nums ${row.available ? "text-slate-950" : "text-slate-400"}`}>
                  {row.available ? `${row.points}/${row.max}` : `${scoringLabel("Not set", scoringLanguage)} /${row.max}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        {!compact && (
          <div className="mt-3 rounded-md border border-emerald-100 bg-white/70 p-3 text-xs leading-5 text-emerald-900">
            <span className="font-semibold">{scoringLabel("How to read this:", scoringLanguage)}</span>{" "}
            {scoringLabel("higher semantic points mean the CV is broadly relevant; higher skill points mean the required stack is better covered; experience evidence checks whether skills appear in work/project context; language or education adds a support signal.", scoringLanguage)}
          </div>
        )}

        {!compact && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950 shadow-sm">
            <span className="font-bold">Important:</span>{" "}
            AI enrichment explains selected candidates, but the recruiter remains responsible for reviewing evidence and making the final hiring decision.
          </div>
        )}

        {!compact && needsHumanReview && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
            <span className="font-semibold">Needs recruiter review:</span>{" "}
            The CV looks broadly related to the job, but RecruitGlance could not confirm the required criteria from deterministic evidence. Review the CV before making a decision.
          </div>
        )}

        {!compact && (
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {rows.map((row) => (
              <div key={`${row.label}-detail`} className="rounded-md bg-white/70 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{row.label}</span>
                  <span className={`text-sm font-bold ${row.available ? "text-emerald-800" : "text-slate-400"}`}>
                    {row.available ? `${row.points}/${row.max}` : scoringLabel("Not set", scoringLanguage)}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${row.available ? "bg-emerald-700" : "bg-slate-200"}`}
                    style={{ width: `${row.available ? Math.max(0, Math.min(100, (row.points / row.max) * 100)) : 0}%` }}
                  />
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-500">{scoringLabel(row.detail, scoringLanguage)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const buildScoreExplanation = (candidate) => {
    const score = Math.round(candidate.match_score || 0);
    const matchingSkills = getDetectedSignals(candidate);
    const missingSkills = getUnconfirmedSignals(candidate);
    const scoringBreakdown = getScoringBreakdown(candidate);
    const explanationParts = [];
    const asPercent = (value) => Math.round(Math.max(0, Math.min(100, Number(value) || 0)));

    if (scoringBreakdown) {
      const baseScore = asPercent(scoringBreakdown.base_score);

      if (scoringBreakdown.must_score !== null && scoringBreakdown.must_score !== undefined) {
        const mustScore = asPercent(scoringBreakdown.must_score);
        const failedMustCount = scoringBreakdown.failed_must_count || 0;

        explanationParts.push(
          failedMustCount > 0
            ? `The score is limited because ${failedMustCount} must-have requirement${failedMustCount === 1 ? "" : "s"} did not meet the target.`
            : `The must-have criteria are mostly covered, with a must-have fit of ${mustScore}%.`
        );
      }

      if (scoringBreakdown.preferred_score !== null && scoringBreakdown.preferred_score !== undefined) {
        explanationParts.push(`Preferred criteria show ${asPercent(scoringBreakdown.preferred_score)}% bonus fit.`);
      }

      if (scoringBreakdown.experience_score !== null && scoringBreakdown.experience_score !== undefined) {
        explanationParts.push(`Experience evidence is ${asPercent(scoringBreakdown.experience_score)}%, based on whether required skills appear in work or project context.`);
      }

      explanationParts.push(`The baseline CV-to-job match before final weighting was ${baseScore}%.`);
    } else {
      explanationParts.push(
        "The score is based on semantic relevance, detected skill coverage, experience evidence, and role-specific overlap."
      );
    }

    if (matchingSkills.length) {
      explanationParts.push(
        `Matched signals include ${matchingSkills.slice(0, 5).join(", ")}.`
      );
    }

    if (missingSkills.length) {
      explanationParts.push(
        `Main gaps detected: ${missingSkills.slice(0, 5).join(", ")}.`
      );
    } else {
      explanationParts.push("No major missing skills were detected from the job description.");
    }

    return `This CV received ${score}% because ${explanationParts.join(" ")}`;
  };

  const getRecruiterNote = (candidate) => (
    candidate.recruiter_note === "Scanned from candidate library"
      ? ""
      : candidate.recruiter_note || ""
  );

  const hasAiAnalysis = (candidate) => (
    Boolean(getCandidateAiSummary(candidate)) ||
    getAiStrengths(candidate).length > 0 ||
    getAiGaps(candidate).length > 0
  );

  const topAiEnrichmentComplete = () => (
    topCandidates.length > 0 && topCandidates.every((candidate) => hasAiAnalysis(candidate))
  );

  const getScanStageState = () => {
    if (scanning) {
      return {
        activeKey: "scoring",
        progress: 45,
        title: "Scanning selected candidates",
        subtitle: "Checking candidate records, scoring skills, and ranking applicants.",
      };
    }

    if (results && enrichmentProgress.active) {
      return {
        activeKey: "enriching",
        progress: 82,
        title: "AI enriching top candidates",
        subtitle: `${enrichmentProgress.enriched} / ${enrichmentProgress.total} top candidates enriched.`,
      };
    }

    if (results && enrichmentProgress.total > 0 && enrichmentProgress.enriched < enrichmentProgress.total) {
      return {
        activeKey: "enriching",
        progress: 92,
        title: "Ranking ready, AI enrichment still processing",
        subtitle: `${enrichmentProgress.enriched} / ${enrichmentProgress.total} top candidates enriched. Results can be reviewed while the remaining AI details finish.`,
      };
    }

    if (results) {
      return {
        activeKey: "complete",
        progress: 100,
        title: "Ranking complete",
        subtitle: "Results are ready for review.",
      };
    }

    return null;
  };

  const renderScanStageProgress = () => {
    const stageState = getScanStageState();
    if (!stageState) return null;

    const stages = [
      { key: "preparing", label: "Preparing" },
      { key: "scoring", label: "Scoring" },
      { key: "ranking", label: "Ranking" },
      { key: "enriching", label: "AI Enriching" },
      { key: "complete", label: "Complete" },
    ];
    const activeIndex = stages.findIndex((stage) => stage.key === stageState.activeKey);

    return (
      <div className="overflow-hidden rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-950 p-5 text-white shadow-lg">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-300/30 bg-emerald-400/15">
              <Brain className="animate-pulse text-emerald-200" size={24} />
            </div>
            <div>
              <p className="text-base font-semibold">{stageState.title}</p>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-emerald-50/80">{stageState.subtitle}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-emerald-50">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  {selectedIds.length} selected
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">
                  Job-based scan
                </span>
                {(scanStartedAt || scanning || enrichmentProgress.active) && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1">
                    <Clock3 size={13} className="text-emerald-200" />
                    Running {formatElapsedTime(scanElapsedSeconds)}
                  </span>
                )}
                {(enrichmentProgress.total > 0 || topCandidates.length > 0) && (
                  <span className={`rounded-full border border-white/15 bg-white/10 px-3 py-1 ${
                    enrichmentProgress.active ? "animate-pulse" : ""
                  }`}>
                    {topAiEnrichmentComplete()
                      ? `${topCandidates.length} / ${topCandidates.length} AI enriched`
                      : `${enrichmentProgress.enriched || 0} / ${enrichmentProgress.total || topCandidates.length} AI enriching`}
                  </span>
                )}
              </div>
            </div>
          </div>

          <span className="w-fit rounded-full border border-emerald-300/40 bg-white px-4 py-2 text-sm font-bold text-emerald-900 shadow-sm">
            {stageState.progress}%
          </span>
        </div>

        <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-300 via-lime-200 to-emerald-100 shadow-[0_0_18px_rgba(110,231,183,0.55)] transition-all duration-700 ease-out"
            style={{ width: `${stageState.progress}%` }}
          />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-5">
          {stages.map((stage, index) => {
            const complete = index < activeIndex || stageState.activeKey === "complete";
            const active = index === activeIndex && stageState.activeKey !== "complete";

            return (
              <div
                key={stage.key}
                className={`flex min-h-[52px] items-center justify-center rounded-lg border px-3 text-center text-xs font-semibold transition ${
                  complete
                    ? "border-emerald-300/40 bg-emerald-300/15 text-emerald-50"
                    : active
                    ? "animate-pulse border-lime-200 bg-lime-200 text-emerald-950 shadow-[0_0_18px_rgba(190,242,100,0.35)]"
                    : "border-white/10 bg-white/5 text-emerald-50/45"
                }`}
              >
                <span>{stage.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const toggleResultSelection = (scanId) => {
    setSelectedResultIds((prev) =>
      prev.includes(scanId)
        ? prev.filter((id) => id !== scanId)
        : [...prev, scanId]
    );
  };

  const toggleAllResults = () => {
    if (!rankedCandidates.length) return;

    if (selectedResultIds.length === rankedCandidates.length) {
      setSelectedResultIds([]);
      return;
    }

    setSelectedResultIds(rankedCandidates.map((candidate) => candidate.scan_id));
  };

  const toggleShortlist = async (candidate) => {
    try {
      const res = await apiFetch(`/dashboard/my_scans/${candidate.scan_id}/shortlist`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Shortlist update", data?.detail));
        return;
      }

      setShortlisted((prev) => (
        data.is_shortlisted
          ? [...prev.filter((item) => item.scan_id !== candidate.scan_id), candidate]
          : prev.filter((item) => item.scan_id !== candidate.scan_id)
      ));

      setResults((prev) => {
        if (!prev) return prev;

        const updateList = (list = []) =>
          list.map((item) =>
            item.scan_id === candidate.scan_id
              ? { ...item, is_shortlisted: data.is_shortlisted }
              : item
          );

        return {
          ...prev,
          top_candidates: updateList(prev.top_candidates),
          remaining_candidates: updateList(prev.remaining_candidates),
          ranked_candidates: updateList(prev.ranked_candidates),
        };
      });
    } catch (err) {
      toast.error(actionFailureMessage("Shortlist update"));
    }
  };

  const saveNote = async (scanId, note) => {
    try {
      const res = await apiFetch(`/dashboard/my_scans/${scanId}/note`, {
        method: "POST",
        body: JSON.stringify({ note: note || "" }),
      });

      if (!res.ok) {
        const data = await res.json();
        toast.error(actionFailureMessage("Save note", data?.detail));
        return;
      }

      setNotes((prev) => ({
        ...prev,
        [scanId]: note || "",
      }));
      toast.success("Note saved.");
    } catch (err) {
      toast.error(actionFailureMessage("Save note"));
    }
  };

  const toggleAnalytics = async (scanId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [scanId]: !prev[scanId],
    }));

    if (analyticsData[scanId]) return;

    try {
      setLoadingAnalytics(scanId);
      const res = await apiFetch(`/scan/${scanId}/summary`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("AI analysis", data?.detail));
        return;
      }

      setAnalyticsData((prev) => ({
        ...prev,
        [scanId]: data,
      }));

      if (data?.scans_left !== undefined) {
        setScansLeft(data.scans_left);
      }

      if (data?.charged) {
        toast.success("AI analysis generated. 1 scan was used from your quota.");
      }
    } catch (err) {
      toast.error(actionFailureMessage("AI analysis"));
    } finally {
      setLoadingAnalytics(null);
    }
  };

  const updateCandidateInterviewQuestions = (scanId, interviewQuestions) => {
    setAnalyticsData((prev) => ({
      ...prev,
      [scanId]: {
        ...(prev[scanId] || {}),
        interview_questions: interviewQuestions || [],
      },
    }));

    setResults((prev) => {
      if (!prev) return prev;

      const updateList = (list = []) =>
        list.map((candidate) => {
          if (candidate.scan_id !== scanId) return candidate;

          return {
            ...candidate,
            interview_questions: interviewQuestions || [],
            candidate_summary: {
              ...(candidate.candidate_summary || {}),
              interview_questions: interviewQuestions || [],
            },
          };
        });

      return {
        ...prev,
        top_candidates: updateList(prev.top_candidates),
        remaining_candidates: updateList(prev.remaining_candidates),
        ranked_candidates: updateList(prev.ranked_candidates),
      };
    });
  };

  const requestInterviewQuestions = async (scanId) => {
    setLoadingInterviewQuestions((prev) => ({ ...prev, [scanId]: true }));

    try {
      const res = await apiFetch(`/scan/${scanId}/interview-questions`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Generate interview questions", data?.detail));
        return;
      }

      updateCandidateInterviewQuestions(scanId, data.interview_questions || []);
      toast.success("Interview questions generated.");
    } catch (err) {
      toast.error(actionFailureMessage("Generate interview questions"));
    } finally {
      setLoadingInterviewQuestions((prev) => ({ ...prev, [scanId]: false }));
    }
  };

  const toggleResultCard = (scanId) => {
    setExpandedResultCards((prev) => ({
      ...prev,
      [scanId]: !prev[scanId],
    }));
  };

  const openResultCard = (scanId) => {
    setExpandedResultCards((prev) => ({
      ...prev,
      [scanId]: true,
    }));

    setTimeout(() => {
      document
        .querySelector(`[data-result-scan-id="${scanId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const renderResultCard = (candidate, index) => {
    const score = Math.round(candidate.match_score || 0);
    const matchingSkills = getMatchingSkills(candidate);
    const missingSkills = getMissingSkills(candidate);
    const aiStrengths = getAiStrengths(candidate);
    const aiGaps = getAiGaps(candidate);
    const aiSummary = getCandidateAiSummary(candidate);
    const interviewQuestions = getInterviewQuestions(candidate);
    const skillEvidence = getSkillEvidence(candidate);
    const languageMatch = getLanguageMatch(candidate);
    const scoringBreakdown = getScoringBreakdown(candidate);
    const applicantTitles = getApplicantTitles(candidate);
    const topQualification = getTopQualification(candidate);
    const scoreExplanation = buildScoreExplanation(candidate);
    const alreadyEnriched = hasAiAnalysis(candidate);
    const isShortlisted = shortlisted.some((item) => item.scan_id === candidate.scan_id);
    const isExpanded = Boolean(expandedResultCards[candidate.scan_id]);
    const hasCriteriaEvidence =
      (scoringBreakdown?.must_have || []).length > 0 || (scoringBreakdown?.preferred || []).length > 0;
    const hasAiEvidence =
      aiSummary || aiStrengths.length > 0 || aiGaps.length > 0 || skillEvidence.length > 0 || interviewQuestions.length > 0;
    const resultDecisionTabs = [
      { id: "technical", label: "Skills & Evidence", count: matchingSkills.length + missingSkills.length },
      { id: "ai", label: hasAiEvidence ? "AI Review" : "AI Review · Not generated", count: hasAiEvidence ? 1 : 0 },
      ...(languageMatch.length > 0 ? [{ id: "language", label: "Language / Education", count: languageMatch.length }] : []),
      ...(hasCriteriaEvidence ? [{ id: "role", label: "Criteria Match", count: 1 }] : []),
    ];
    const selectedDecisionTab = resultDecisionTabs.some(
      (tab) => tab.id === activeDecisionTabs[candidate.scan_id] && !tab.disabled
    )
      ? activeDecisionTabs[candidate.scan_id]
      : "technical";
    const decisionTabClass = (tab) =>
      `inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border px-3 text-xs font-bold transition ${
        selectedDecisionTab === tab.id
          ? "border-emerald-900 bg-emerald-950 text-white shadow-sm"
          : tab.disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
          : "border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
      }`;

    return (
      <article
        key={candidate.scan_id}
        data-result-scan-id={candidate.scan_id}
        className={`rounded-lg bg-white p-5 shadow-sm transition ${
          isExpanded ? "border-2 border-emerald-300 ring-2 ring-emerald-50" : "border border-slate-200"
        }`}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleResultCard(candidate.scan_id)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleResultCard(candidate.scan_id);
            }
          }}
          className="cursor-pointer"
        >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <input
              type="checkbox"
              checked={selectedResultIds.includes(candidate.scan_id)}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleResultSelection(candidate.scan_id)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-950"
            />
            <div className="min-w-0">
              <span className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                Rank #{index + 1}
              </span>
              {candidate.is_duplicate && (
                <span className="ml-2 inline-flex rounded-md bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                  Existing scan
                </span>
              )}
              <h3 className="mt-2 truncate text-lg font-bold text-slate-950">{candidate.filename}</h3>
              <span className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${decisionBadgeClass(score)}`}>
                {decisionLabel(score)}
              </span>
              <p className="mt-1 text-xs font-medium text-slate-400">
                Scanned {formatDate(candidate.created_at)}
              </p>
              {isExpanded && (topQualification || applicantTitles.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {topQualification && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                      Education & certifications: {topQualification}
                    </span>
                  )}
                  {applicantTitles.slice(0, 4).map((title) => (
                    <span key={`${candidate.scan_id}-title-${title}`} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                      {title}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <span className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreClass(score)}`}>
              {score}%
            </span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleShortlist(candidate);
              }}
              className={`inline-flex min-h-[34px] items-center justify-center rounded-lg px-3 text-sm font-semibold transition ${
                isShortlisted
                  ? "bg-amber-300 text-slate-950 hover:bg-amber-400"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {isShortlisted ? "★ Shortlisted" : "☆ Shortlist"}
            </button>
            <span className="text-xs font-semibold text-slate-500">
              {isExpanded ? "Hide details" : "Show details"}
            </span>
          </div>
        </div>
        </div>

        {!isExpanded && (
          <div className="mt-4">
            {renderScoreBreakdown(candidate, score, true)}
          </div>
        )}

        {isExpanded && (
          <>
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-950">{scoringLabel("Why this score?", scoringLanguage)}</h4>
          <p className="mt-2 text-sm leading-6 text-slate-600">{scoreExplanation}</p>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-2">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {resultDecisionTabs.map((tab) => (
              <button
                key={`${candidate.scan_id}-decision-tab-${tab.id}`}
                type="button"
                disabled={tab.disabled}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!tab.disabled) {
                    const scrollY = window.scrollY;
                    setActiveDecisionTabs((prev) => ({
                      ...prev,
                      [candidate.scan_id]: tab.id,
                    }));
                    requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: window.scrollX }));
                  }
                }}
                className={decisionTabClass(tab)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {selectedDecisionTab === "technical" && (
        <div className="mt-3 rounded-xl border-2 border-emerald-400 bg-emerald-100/60 p-4 shadow-[0_0_0_3px_rgba(16,185,129,0.10)]">
          <div className="mb-4 rounded-lg bg-emerald-950 px-4 py-3 text-white shadow-sm">
            <h4 className="mt-1 text-base font-bold">Technical Fit</h4>
            <p className="mt-1 text-xs leading-5 text-emerald-100">Skills, requirement evidence, score contribution, and technical gaps.</p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Detected signals</h4>
              <p className="mb-3 text-xs leading-5 text-slate-500">Confirmed by skill extraction, recruiter criteria evidence, or semantic relevance.</p>
              <div className="flex flex-wrap gap-2">
                {matchingSkills.length > 0 ? (
                  matchingSkills.map((skill) => (
                    <span key={`${candidate.scan_id}-match-${skill}`} className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      {skill}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No strong matches detected</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Unconfirmed signals</h4>
              <p className="mb-3 text-xs leading-5 text-slate-500">Required or useful job signals not clearly confirmed yet.</p>
              <div className="flex flex-wrap gap-2">
                {missingSkills.length > 0 ? (
                  missingSkills.map((skill) => (
                    <span key={`${candidate.scan_id}-gap-${skill}`} className="rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      {skill}
                    </span>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No major gaps detected</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4">
            {renderScoreBreakdown(candidate, score)}
          </div>
        </div>
        )}

        {selectedDecisionTab === "role" && (
        <div className="mt-3 rounded-xl border-2 border-amber-400 bg-amber-100/60 p-4 shadow-[0_0_0_3px_rgba(245,158,11,0.12)]">
          <div className="mb-4 rounded-lg bg-amber-700 px-4 py-3 text-white shadow-sm">
            <h4 className="mt-1 text-base font-bold">Role & Responsibility Fit</h4>
            <p className="mt-1 text-xs leading-5 text-amber-50">
              Seniority, ownership, delivery context, collaboration, and leadership-style signals are reflected through experience evidence and recruiter notes.
            </p>
          </div>

          {hasCriteriaEvidence && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-white p-3">
              <h4 className="text-sm font-semibold text-emerald-950">Criteria Evidence</h4>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {(scoringBreakdown.must_have || []).map((item) => (
                  <div key={`${candidate.scan_id}-must-${item.skill}`} className="rounded-md bg-emerald-50/70 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">{item.skill}</span>
                      <span className={`rounded-full px-2 py-1 text-xs font-bold ${
                        item.passed ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                      }`}>
                        {Math.round(item.score || 0)}% / {Math.round(item.required || 0)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${item.passed ? "bg-emerald-600" : "bg-rose-500"}`}
                        style={{ width: `${Math.max(0, Math.min(100, item.score || 0))}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Must-have {item.passed ? "passed" : "below target"}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      {localizedCriteriaReason(item.reason || "No clear evidence found in CV.", scoringLanguage)}
                    </p>
                  </div>
                ))}

                {(scoringBreakdown.preferred || []).map((item) => (
                  <div key={`${candidate.scan_id}-preferred-${item.skill}`} className="rounded-md bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">{item.skill}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700">
                        {Math.round(item.score || 0)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-slate-700"
                        style={{ width: `${Math.max(0, Math.min(100, item.score || 0))}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Preferred {item.found ? "found" : "not found"}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      {localizedCriteriaReason(item.reason || "No clear evidence found in CV.", scoringLanguage)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {selectedDecisionTab === "language" && languageMatch.length > 0 && (
          <div className="mt-3 rounded-xl border-2 border-teal-400 bg-teal-100/60 p-4 shadow-[0_0_0_3px_rgba(20,184,166,0.12)]">
            <div className="mb-4 rounded-lg bg-teal-800 px-4 py-3 text-white shadow-sm">
              <h4 className="mt-1 text-base font-bold">Language & Qualification Fit</h4>
              <p className="mt-1 text-xs leading-5 text-teal-50">Language requirements, degree evidence, certifications, and qualification signals.</p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {languageMatch.map((item, itemIndex) => {
                const fitScore = getLanguageFitScore(item);

                return (
                  <div key={`${candidate.scan_id}-language-match-${itemIndex}`} className="rounded-md bg-white/80 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-900">
                        {item.language || "Language"}
                      </span>
                      <span className="text-sm font-bold text-emerald-800">
                        {fitScore}%
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide">
                      {item.required_level && (
                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800">
                          Required: {item.required_level}
                        </span>
                      )}
                      <span className="rounded-full bg-white px-2 py-1 text-emerald-700">
                        Match: {item.status || "language signal"}
                      </span>
                    </div>
                    {item.evidence && (
                      <p className="mt-1 text-xs leading-5 text-slate-600">
                        {item.evidence}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {selectedDecisionTab === "ai" && (
          <div className="mt-3 rounded-xl border-2 border-indigo-400 bg-indigo-100/60 p-4 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]">
            <div className="mb-4 rounded-lg bg-indigo-950 px-4 py-3 text-white shadow-sm">
              <h4 className="mt-1 text-base font-bold">AI Evidence</h4>
              <p className="mt-1 text-xs leading-5 text-indigo-100">
                {hasAiEvidence
                  ? "AI-generated reasoning for this candidate, including summary, evidence scores, gaps, and optional interview questions."
                  : "Request an AI review only when the recruiter wants deeper candidate evidence."}
              </p>
            </div>

            {!hasAiEvidence && !alreadyEnriched && (
              <div className="rounded-lg border border-indigo-200 bg-white p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-indigo-950">AI analysis not generated yet</h4>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Request AI analysis when you want a deeper explanation for this candidate. This can use scan quota for non-enriched candidates.
                </p>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleAnalytics(candidate.scan_id);
                  }}
                  className="mt-3 inline-flex min-h-[34px] items-center justify-center rounded-lg bg-emerald-800 px-3 text-sm font-semibold text-white transition hover:bg-emerald-900"
                >
                  {expandedRows[candidate.scan_id] ? "Hide AI Analysis" : "Get AI Analysis"}
                </button>

                {expandedRows[candidate.scan_id] && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    {loadingAnalytics === candidate.scan_id ? (
                      <p className="text-sm text-slate-500">Loading AI analysis...</p>
                    ) : (
                      <p className="text-sm text-slate-500">No AI summary available yet.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {aiSummary && (
              <div className="rounded-lg border border-sky-200 bg-white p-3">
                <p className="text-sm leading-6 text-sky-900">
                  <strong>AI Summary:</strong> {aiSummary}
                </p>
              </div>
            )}

            {(aiStrengths.length > 0 || aiGaps.length > 0) && (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-emerald-200 bg-white p-3">
                  <h4 className="mb-2 text-sm font-semibold text-emerald-900">{scoringLabel("AI Matching Evidence", scoringLanguage)}</h4>
                  {aiStrengths.length > 0 ? (
                    <ul className="space-y-2 text-sm leading-6 text-emerald-800">
                      {aiStrengths.map((item, itemIndex) => (
                        <li key={`${candidate.scan_id}-ai-strength-${itemIndex}`}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-emerald-700">No AI evidence generated yet.</p>
                  )}
                </div>

                <div className="rounded-lg border border-rose-200 bg-white p-3">
                  <h4 className="mb-2 text-sm font-semibold text-rose-900">{scoringLabel("AI Skill Gaps", scoringLanguage)}</h4>
                  {aiGaps.length > 0 ? (
                    <ul className="space-y-2 text-sm leading-6 text-rose-800">
                      {aiGaps.map((item, itemIndex) => (
                        <li key={`${candidate.scan_id}-ai-gap-${itemIndex}`}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-rose-700">No AI gaps generated yet.</p>
                  )}
                </div>
              </div>
            )}

            {skillEvidence.length > 0 && (
              <div className="mt-4 rounded-lg border border-indigo-200 bg-white p-3">
                <h4 className="mb-3 text-sm font-semibold text-indigo-950">
                  Skill Evidence Score
                </h4>
                <div className="grid gap-3 md:grid-cols-2">
                  {skillEvidence.map((item, itemIndex) => {
                    const confidence = Math.max(0, Math.min(100, Number(item.confidence) || 0));

                    return (
                      <div key={`${candidate.scan_id}-skill-evidence-${itemIndex}`} className="rounded-md bg-indigo-50/70 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-slate-900">
                            {item.skill || "Skill"}
                          </span>
                          <span className="text-sm font-bold text-indigo-800">
                            {confidence}%
                          </span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-indigo-100">
                          <div
                            className="h-full rounded-full bg-indigo-600"
                            style={{ width: `${confidence}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                          {item.status || "evidence"}
                        </p>
                        {item.evidence && (
                          <p className="mt-1 text-xs leading-5 text-slate-600">
                            {item.evidence}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(alreadyEnriched || interviewQuestions.length > 0) && (
              <div className="mt-4 rounded-lg border border-indigo-200 bg-white p-3">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h4 className="text-sm font-semibold text-indigo-950">
                    Suggested Interview Questions
                  </h4>
                  {!interviewQuestions.length && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        requestInterviewQuestions(candidate.scan_id);
                      }}
                      disabled={Boolean(loadingInterviewQuestions[candidate.scan_id])}
                      className="inline-flex min-h-[32px] items-center justify-center rounded-lg bg-indigo-700 px-3 text-xs font-semibold text-white transition hover:bg-indigo-800 disabled:opacity-60"
                    >
                      {loadingInterviewQuestions[candidate.scan_id] ? "Generating..." : "Generate questions"}
                    </button>
                  )}
                </div>
                {interviewQuestions.length > 0 ? (
                  <div className="space-y-3">
                    {interviewQuestions.map((item, itemIndex) => (
                      <div key={`${candidate.scan_id}-interview-${itemIndex}`} className="rounded-md bg-indigo-50/70 p-3">
                        {item.gap && (
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                            Gap: {item.gap}
                          </p>
                        )}
                        <ul className="space-y-2 text-sm leading-6 text-indigo-950">
                          {(item.questions || []).map((question, questionIndex) => (
                            <li key={`${candidate.scan_id}-interview-${itemIndex}-${questionIndex}`}>
                              {question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-md bg-indigo-50/70 p-3 text-sm text-indigo-900">
                    Generate questions only when you want to interview this AI-enriched candidate.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="mb-2 text-sm font-semibold text-amber-900">Recruiter Notes</p>
            <textarea
              placeholder="Add your notes..."
              className="w-full resize-y rounded-lg border border-amber-200 bg-white p-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              rows={4}
              value={notes[candidate.scan_id] ?? getRecruiterNote(candidate)}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) =>
                setNotes((prev) => ({
                  ...prev,
                  [candidate.scan_id]: event.target.value,
                }))
              }
              onBlur={(event) => saveNote(candidate.scan_id, event.target.value)}
            />
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                saveNote(candidate.scan_id, notes[candidate.scan_id] ?? "");
              }}
              className="mt-2 inline-flex min-h-[32px] items-center justify-center rounded-lg bg-amber-500 px-3 text-xs font-semibold text-white transition hover:bg-amber-600"
            >
              Save Note
            </button>
          </div>
        </div>
          </>
        )}
      </article>
    );
  };

  const renderCandidateTable = (title, subtitle, list, scanIdGetter, emptyText) => (
    <div className="rounded-lg border border-slate-200">
      <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold text-slate-950">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
          {list.length} candidate{list.length === 1 ? "" : "s"}
        </span>
      </div>

      {list.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th className="py-3 pl-4 pr-4">Select</th>
                <th className="py-3 pr-4">Candidate</th>
                <th className="py-3 pr-4">Current Best</th>
                <th className="py-3 pr-4">Skills</th>
                <th className="py-3 pr-4">Linked Jobs</th>
              </tr>
            </thead>
            <tbody>
              {list.map((candidate) => {
                const scanId = scanIdGetter(candidate);
                const isSelected = selectedIds.includes(scanId);
                const isReady = isCandidateReady(candidate);

                return (
                  <tr
                    key={`${title}-${candidate.candidate_id}`}
                    className={`border-b last:border-0 ${
                      isHighlightedCandidate(candidate)
                        ? "border-emerald-100 bg-emerald-50/60"
                        : "border-slate-100"
                    }`}
                  >
                    <td className="py-4 pl-4 pr-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isReady}
                        onChange={() => toggleCandidate(scanId)}
                        className="h-4 w-4 rounded border-slate-300 text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </td>
                    <td className="max-w-[260px] py-4 pr-4">
                      <p className="truncate font-semibold text-slate-950">{candidate.name}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{candidate.latest_filename}</p>
                      <p className="mt-1 text-xs font-medium text-slate-400">
                        Imported {formatDate(candidate.imported_at)}
                      </p>
                      {isHighlightedCandidate(candidate) && (
                        <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                          Newly imported
                        </span>
                      )}
                      {!isReady && (
                        <span className={`mt-2 ml-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                          getEnrichmentStatus(candidate) === "failed"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {getEnrichmentStatus(candidate) === "failed" ? "Enrichment failed" : "Preparing for scan"}
                        </span>
                      )}
                    </td>
                    <td className="py-4 pr-4">
                      <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.best_score || 0)}`}>
                        {Math.round(candidate.best_score || 0)}%
                      </span>
                    </td>
                    <td className="max-w-[300px] py-4 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {(candidate.skills || []).slice(0, 5).map((skill) => (
                          <span key={`${candidate.candidate_id}-${title}-${skill}`} className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                            {skill}
                          </span>
                        ))}
                        {(candidate.skills || []).length === 0 && (
                          <span className="text-xs text-slate-400">No skills detected</span>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[260px] py-4 pr-4 text-slate-600">
                      <span className="block truncate">
                        {(candidate.jobs || []).map((item) => item.job_name).join(", ") || "No job linked"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const leftPanel = <RecruiterSidebar activeItem={routeJobId ? "jobs" : "bulk"} user={user} />;

  const mainContent = (
    <>
      <section className="overflow-hidden rounded-lg border border-emerald-900/20 bg-[#031b14] shadow-sm">
        <div className="flex flex-col gap-4 p-5 text-white lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
              <FileStack size={14} />
              Screening workspace
            </div>
            <h2 className="mt-4 text-xl font-semibold">Scan Candidate Library</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-50/80">
              Select candidates that are already imported or previously scanned, then rank them against this job without uploading files again.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-emerald-300/50 bg-white px-4 text-sm font-semibold text-emerald-950 shadow-sm transition hover:bg-emerald-50"
              >
                <FileStack size={16} />
                New Scan
              </button>
              <button
                type="button"
                onClick={() => navigate("/scan-history")}
                className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-emerald-300/30 bg-emerald-400/10 px-4 text-sm font-semibold text-emerald-50 transition hover:bg-emerald-400/15"
              >
                <CalendarClock size={16} />
                Scan History
              </button>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 lg:w-[320px]">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-100/80">Select job</span>
              <select
                value={selectedJobId}
                onChange={(event) => {
                  setSelectedJobId(event.target.value);
                  setSelectedIds([]);
                  setResults(null);
                  setJobScannedFilenames([]);
                }}
                className="mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-900/30"
              >
                <option value="">Choose a job to scan applicants</option>
                {isSelectedJobArchived && selectedJobId && (
                  <option value={selectedJobId}>
                    {job?.name || "Archived job"} (Archived)
                  </option>
                )}
                {jobs.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => selectedJobId ? navigate(`/jobs/${selectedJobId}`) : navigate("/jobs")}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg border border-emerald-300/30 bg-emerald-400/10 px-4 text-sm font-semibold text-emerald-50 transition hover:bg-emerald-400/15"
            >
              <ArrowLeft size={16} />
              Job Details
            </button>
            <button
              type="button"
              onClick={handleScanSelected}
              disabled={scanning || selectedIds.length === 0 || quotaExceeded || isSelectedJobArchived}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckSquare size={16} />
              {scanning ? "Scanning..." : `Scan Selected (${selectedIds.length})`}
            </button>
            </div>
          </div>
        </div>

        <div className="mx-5 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm leading-6 text-emerald-50">
          You can scan up to {MAX_BULK_SCAN_CANDIDATES} candidates at once. Selected{" "}
          <span className={bulkScanLimitExceeded ? "font-bold text-rose-200" : "font-semibold"}>
            {selectedIds.length}/{MAX_BULK_SCAN_CANDIDATES}
          </span>
          {bulkScanLimitExceeded && (
            <span className="ml-1 font-semibold text-rose-200">
              Please reduce the selection or split the scan into batches.
            </span>
          )}
        </div>

        {quotaExceeded && (
          <div className="mx-5 mt-5 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800">
            Your scan quota is finished for this billing cycle. Please wait until your quota refreshes or upgrade your plan.
          </div>
        )}

        {isSelectedJobArchived && (
          <div className="mx-5 mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
            This job is archived. Restore it from the Job Details page before scanning more candidates.
          </div>
        )}

        {(scanning || results || enrichmentProgress.active) && (
          <div ref={progressRef} className="mx-5 mt-5 scroll-mt-24">
            {renderScanStageProgress()}
          </div>
        )}

        <div className="m-5 rounded-lg border border-white/10 bg-white p-4 text-slate-900">
          <p className="text-sm font-semibold text-slate-900">{job?.name || "Selected job"}</p>
          <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">
            {job?.description || "No job description saved."}
          </p>
        </div>
      </section>

      {isNewlyImportedFlow && newlyImportedCandidates.length > 0 && (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                Newly imported
              </span>
              <h2 className="mt-3 text-lg font-semibold text-emerald-950">
                {newlyImportedCandidates.length} newly imported candidate{newlyImportedCandidates.length === 1 ? "" : "s"} selected
              </h2>
              <p className="mt-1 text-sm leading-6 text-emerald-800">
                These candidates came from the import flow and are preselected for this scan.
              </p>
            </div>

            <button
              type="button"
              onClick={handleScanSelected}
              disabled={scanning || selectedIds.length === 0 || quotaExceeded || isSelectedJobArchived}
              className="inline-flex min-h-[40px] shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-50"
            >
              <CheckSquare size={16} />
              {scanning ? "Scanning..." : `Scan Selected (${selectedIds.length})`}
            </button>
          </div>

          {bulkScanLimitExceeded && (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
              This scan has {selectedIds.length} candidates selected. The limit is {MAX_BULK_SCAN_CANDIDATES} per scan.
            </div>
          )}

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {newlyImportedCandidates.slice(0, 6).map((candidate) => (
              <div key={`newly-imported-${candidate.candidate_id}`} className="rounded-lg border border-emerald-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-950">{candidate.name}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">{candidate.latest_filename}</p>
                    <p className="mt-1 text-xs font-medium text-emerald-700">
                      Imported {formatDate(candidate.imported_at)}
                    </p>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                    Newly imported
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {results && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Scan Results</h2>
              <p className="mt-1 text-sm text-slate-500">
                Ranked results are shown first so recruiters can review the strongest matches before changing candidate selection.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/jobs/${selectedJobId}`)}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              View Job Pipeline
            </button>
          </div>

          <AiDecisionNotice className="mb-5" />

          <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-slate-50 to-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-950">Current Top Matches</h3>
                <p className="mt-1 text-sm text-slate-500">
                  The strongest candidates from this scan, ranked by criteria match and enriched with AI reasoning.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 self-start">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
                  topAiEnrichmentComplete()
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "animate-pulse border-amber-200 bg-amber-50 text-amber-700"
                }`}>
                  <Brain size={14} />
                  {topAiEnrichmentComplete() ? "AI enriched" : "AI enriching..."}
                </span>
              </div>
            </div>

            {topCandidates.length === 0 ? (
              <p className="rounded-md border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500">
                No ranked candidates returned.
              </p>
            ) : (
              <div className="grid gap-3 rounded-lg p-1 lg:grid-cols-5">
                  {topCandidates.map((candidate, index) => {
                    const score = Math.round(candidate.match_score || 0);
                    const detectedSignals = getDetectedSignals(candidate);
                    const unconfirmedSignals = getUnconfirmedSignals(candidate);
                    const candidateAiReady = hasAiAnalysis(candidate);

                    return (
                      <article
                        key={`top-${candidate.scan_id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => openResultCard(candidate.scan_id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openResultCard(candidate.scan_id);
                          }
                        }}
                        className={`cursor-pointer rounded-lg border bg-white p-3 shadow-sm ring-1 ring-emerald-50 transition hover:-translate-y-0.5 hover:shadow-md ${
                          expandedResultCards[candidate.scan_id] ? "border-slate-900 ring-2 ring-slate-100" : "border-emerald-200"
                        }`}
                      >
                        <div className="mb-3 flex items-start justify-between gap-2">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-800 text-sm font-bold text-white">
                            #{index + 1}
                          </span>
                          {index === 0 && (
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                              <Trophy size={16} />
                            </span>
                          )}
                        </div>

                        <span className={`mb-2 inline-flex rounded-md px-2 py-1 text-[11px] font-bold ${
                          candidateAiReady
                            ? "bg-emerald-100 text-emerald-800"
                            : "animate-pulse bg-amber-100 text-amber-800"
                        }`}>
                          {candidateAiReady ? "AI enriched" : "AI enriching..."}
                        </span>

                        <p className="line-clamp-2 min-h-[40px] text-sm font-semibold text-slate-900">
                          {candidate.filename}
                        </p>
                        <p className="mt-1 text-xs font-medium text-slate-400">
                          Scanned {formatDate(candidate.created_at)}
                        </p>

                        <div className="mt-3 flex items-center justify-between gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${scoreClass(score)}`}>
                            {score}% match
                          </span>
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${decisionBadgeClass(score)}`}>
                            {decisionLabel(score)}
                          </span>
                        </div>

                        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={`h-full rounded-full ${scoreBarClass(score)}`}
                            style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                          />
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <span className="rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-700">
                            {detectedSignals.length} detected
                          </span>
                          <span className="rounded-md bg-rose-50 px-2 py-1 font-medium text-rose-700">
                            {unconfirmedSignals.length} unconfirmed
                          </span>
                        </div>
                      </article>
                    );
                  })}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={toggleAllResults}
              className="text-left text-sm font-semibold text-slate-600 transition hover:text-slate-950"
            >
              {selectedResultIds.length === rankedCandidates.length ? "Unselect All Results" : "Select All Results"}
            </button>
            <p className="text-sm text-slate-500">
              {selectedResultIds.length} / {rankedCandidates.length} selected for recruiter actions
            </p>
          </div>

          {duplicateCandidates.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
              <p className="font-semibold">
                {duplicateCandidates.length} candidate{duplicateCandidates.length === 1 ? "" : "s"} already had scan results for this job.
              </p>
              <p className="mt-1">
                Existing results were reused and refreshed without consuming new scan quota.
              </p>
            </div>
          )}

          <div className="mt-4 space-y-4">
            {rankedCandidates.map((candidate, index) => renderResultCard(candidate, index))}
          </div>

          {remainingCandidates.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              Current top matches are highlighted above. Remaining candidates continue in the ranked table.
            </p>
          )}
        </section>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Candidate Pool</h2>
            <p className="mt-1 text-sm text-slate-500">
              Candidates are reused from your existing database. Job-linked results are created only after scanning.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search candidates or skills"
                className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 sm:w-72"
              />
            </div>

            <button
              type="button"
              onClick={toggleVisibleCandidates}
              disabled={!selectedJobId || filteredCandidates.length === 0}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Select Visible
            </button>
            <button
              type="button"
              onClick={selectPendingJobCandidates}
              disabled={!selectedJobId || pendingJobApplicantCandidates.length === 0}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Pending Applicants ({pendingJobApplicantCandidates.length})
            </button>
          </div>
        </div>

        {fetching ? (
          <p className="text-sm text-slate-500">Loading candidate library...</p>
        ) : !selectedJobId ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center">
            <Users className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-sm font-medium text-slate-700">Select a job first</p>
            <p className="mt-1 text-sm text-slate-500">The related applicant list will appear automatically after a job is selected.</p>
          </div>
        ) : filteredCandidates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center">
            <Users className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 text-sm font-medium text-slate-700">No candidates available</p>
            <p className="mt-1 text-sm text-slate-500">Import or scan candidates first, then reuse them here.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {renderCandidateTable(
              "Applicants for this job",
              "Candidates already linked to this job from import or previous scans.",
              jobApplicantCandidates,
              getJobApplicantCandidateId,
              "No applicants are linked to this job yet."
            )}

            {renderCandidateTable(
              "General applicants",
              "Reusable talent-pool candidates. Select any of them if you want to scan them against this job.",
              generalApplicantCandidates,
              (candidate) => candidate.candidate_id,
              "No general applicants are available."
            )}
          </div>
        )}
      </section>

    </>
  );

  if (loading || !user) return <p className="text-center mt-20">Loading...</p>;

  return (
    <DashboardLayout
      user={user}
      eyebrow="Reusable candidate flow"
      title="Bulk Scan"
      subtitle="Select a job, review related applicants, optionally add general applicants, and run a fast scan"
      leftPanel={leftPanel}
      mainContent={mainContent}
    />
  );
}
